const BASE = import.meta.env.VITE_API_URL ?? ''

export const ADMIN_KEY_STORAGE = 'sdlc_admin_key'

function getAdminKey(): Record<string, string> {
  const key = localStorage.getItem(ADMIN_KEY_STORAGE) ?? ''
  return key ? { 'X-API-Key': key } : {}
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    headers: { 'Content-Type': 'application/json', ...getAdminKey(), ...init?.headers },
    credentials: 'include',
    ...init,
  })
  if (!res.ok) {
    if (res.status === 401) {
      // Session expired — redirect to OIDC login
      const redirectUri = globalThis.location.origin
      globalThis.location.href = `${BASE}/api/v1/auth/oidc?redirect_uri=${encodeURIComponent(redirectUri)}`
      throw new Error('Session expired — redirecting to login')
    }
    const text = await res.text()
    throw new Error(`${res.status} ${res.statusText}: ${text}`)
  }
  return res.json() as Promise<T>
}

export const api = {
  registry: {
    listRules: (params?: { domain?: string; severity?: string; status?: string }) => {
      const q = new URLSearchParams(params).toString()
      return request<{ rules: import('./types').Rule[]; total: number }>(`/api/v1/registry/rules${q ? '?' + q : ''}`)
    },
    getRule: (id: string) => request<import('./types').Rule>(`/api/v1/registry/rules/${id}`),
    getStats: () => request<import('./types').RegistryStats>('/api/v1/registry/stats'),
    listPrompts: () => request<{ prompts: import('./types').Prompt[]; total: number }>('/api/v1/registry/prompts'),
    getCatalog: () => request<import('./types').CatalogResponse>('/api/v1/registry/catalog'),
    getMcpCatalog: (signal?: AbortSignal) => request<import('../lib/catalog').McpCatalogResponse>('/api/v1/registry/mcp-catalog', { signal }),
    getAgent: (id: string) => request<import('./types').AgentDetail>(`/api/v1/registry/agents/${encodeURIComponent(id)}`),
    getSkill: (id: string) => request<import('./types').SkillDetail>(`/api/v1/registry/skills/${encodeURIComponent(id)}`),
    getTags: () => request<{ tags: import('./types').Tag[] }>('/api/v1/registry/tags'),
    getDeliveryPreview: (profiles: string) => request<import('./types').DeliveryPreview>(`/api/v1/registry/delivery-preview?profiles=${encodeURIComponent(profiles)}`),
    getPlaygroundPreview: (tags: string[], systemId?: string) => {
      const params = new URLSearchParams({ tags: tags.join(',') })
      if (systemId) params.set('system_id', systemId)
      return request<import('./types').PlaygroundResult>(`/api/v1/registry/playground?${params.toString()}`)
    },
  },
  projects: {
    list: () => request<{ projects: import('./types').Project[] }>('/api/v1/projects'),
    get: (id: string) => request<import('./types').Project>(`/api/v1/projects/${id}`),
    getDependencies: (id: string) => request<{ upstream: any[]; downstream: any[] }>(`/api/v1/projects/${id}/dependencies`),
    getAgents: (id: string) => request<{ system_id: string; agents: string[] }>(`/api/v1/projects/${id}/agents`),
    getSkills: (id: string) => request<{ system_id: string; skills: string[] }>(`/api/v1/projects/${id}/skills`),
    getKnowledge: (id: string, aspect: string) => request<{ content: string }>(`/api/v1/projects/${id}/knowledge/${aspect}`),
    // Write operations removed — all mutations via Git PR to canonical repo
    register: (_body: any) => Promise.reject(new Error('Registration is now done via Git PR to sdlc-canonical/systems/')),
    update: (_id: string, _body: any) => Promise.reject(new Error('Updates are now done via Git PR to sdlc-canonical/systems/')),
    delete: (_id: string) => Promise.reject(new Error('Deletion is now done via Git PR to sdlc-canonical/systems/')),
    sync: (_id: string) => Promise.reject(new Error('Sync removed — Git is the single source of truth')),
  },
  analytics: {
    getRuleUsage: () => request<{ rules: import('./types').RuleUsage[]; total_served: number; error?: string }>('/api/v1/analytics/rules'),
    getSummary: () => request<import('./types').AnalyticsSummary>('/api/v1/analytics/summary'),
    getDashboard: () => request<import('./types').DashboardStats>('/api/v1/analytics/dashboard'),
    getRuleAttribution: () => request<import('./types').RuleAttribution>('/api/v1/analytics/rule-attribution'),
  },
  install: {
    listTools: () => request<{ tools: { id: string; name: string; description: string; category: string; homepage: string; command: string; args: string[]; env_keys: string[]; setup_instructions: string; capabilities: Record<string, string>[] }[]; total: number }>('/api/v1/install/tools'),
    generateConfig: (body: { tool_ids: string[]; ide: string; email?: string }) =>
      request<{ ide: string; file_path: string; config: Record<string, unknown>; inputs?: Record<string, unknown>[]; instructions: string }>('/api/v1/install/generate-config', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
  },
}
